import { BaseAIProvider } from './base-provider';

export class GeminiProviderEnhanced extends BaseAIProvider {
  constructor(config) {
    super(config);
    this.apiKey = config.apiKey || process.env.REACT_APP_GEMINI_API_KEY;
    this.baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models';
    this.defaultModel = config.model || 'gemini-2.0-flash-exp';
    this.tools = config.tools || [];
    this.thinkingMode = config.thinkingMode || false;
  }

  async sendMessage(messages, options = {}) {
    const {
      model = this.defaultModel,
      maxTokens = 8192,
      temperature = 0.7,
      systemPrompt = '',
      isJsonOutput = false,
      tools = this.tools,
      thinkingMode = this.thinkingMode,
      files = [],
      onChunk = null // New callback for streaming chunks
    } = options;

    const formattedMessages = this.formatMessages(messages, systemPrompt);

    // Build generation config
    const generationConfig = {
      temperature,
      maxOutputTokens: maxTokens,
      ...(isJsonOutput && { responseMimeType: "application/json" })
    };

    // Add thinking mode if enabled
    if (thinkingMode) {
      generationConfig.responseModalities = ["TEXT"];
      generationConfig.responseSchema = {
        type: "object",
        properties: {
          thinking: {
            type: "string",
            description: "Step-by-step reasoning process"
          },
          answer: {
            type: "string",
            description: "Final answer after thinking"
          }
        }
      };
    }

    // Build request payload
    const payload = {
      contents: formattedMessages,
      generationConfig
    };

    // Add tools if provided
    if (tools && tools.length > 0) {
      payload.tools = [{
        functionDeclarations: tools.map(tool => this.formatToolForGemini(tool))
      }];
    }

    // Add files if provided
    if (files && files.length > 0) {
      payload.contents = this.addFilesToContents(payload.contents, files);
    }

    const url = `${this.baseUrl}/${model}:generateContent?key=${this.apiKey}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(`Gemini API error: ${response.status}. Details: ${JSON.stringify(errorBody)}`);
      }

      const result = await response.json();
      
      // Handle function calls
      if (result.candidates?.[0]?.content?.parts) {
        const parts = result.candidates[0].content.parts;
        const functionCalls = parts.filter(part => part.functionCall);
        
        if (functionCalls.length > 0) {
          // Execute function calls and get results
          const functionResults = await this.executeFunctionCalls(functionCalls);
          
          // Send function results back to continue conversation
          return await this.sendFunctionResults(messages, functionResults, options);
        }
      }

      // Extract response
      if (result.candidates && result.candidates[0]?.content?.parts?.[0]) {
        const text = result.candidates[0].content.parts[0].text;
        
        if (thinkingMode && isJsonOutput) {
          const parsed = JSON.parse(text);
          return {
            success: true,
            content: parsed.answer,
            thinking: parsed.thinking,
            rawResponse: result
          };
        }
        
        return {
          success: true,
          content: isJsonOutput ? JSON.parse(text) : text,
          rawResponse: result
        };
      } else {
        const reason = result.candidates?.[0]?.finishReason || 'Unknown reason';
        throw new Error(`Empty response from Gemini API. Reason: ${reason}`);
      }
    } catch (error) {
      console.error('Gemini API error:', error);
      return {
        success: false,
        error: error.message,
        rawResponse: null
      };
    }
  }

  async sendMessageStream(messages, options = {}) {
    const {
      model = this.defaultModel,
      maxTokens = 8192,
      temperature = 0.7,
      systemPrompt = '',
      isJsonOutput = false,
      tools = this.tools,
      thinkingMode = this.thinkingMode,
      files = [],
      onChunk = null
    } = options;

    const formattedMessages = this.formatMessages(messages, systemPrompt);

    // Build generation config
    const generationConfig = {
      temperature,
      maxOutputTokens: maxTokens,
      ...(isJsonOutput && { responseMimeType: "application/json" })
    };

    // Add thinking mode if enabled
    if (thinkingMode) {
      generationConfig.responseModalities = ["TEXT"];
      generationConfig.responseSchema = {
        type: "object",
        properties: {
          thinking: {
            type: "string",
            description: "Step-by-step reasoning process"
          },
          answer: {
            type: "string",
            description: "Final answer after thinking"
          }
        }
      };
    }

    // Build request payload
    const payload = {
      contents: formattedMessages,
      generationConfig
    };

    // Add tools if provided
    if (tools && tools.length > 0) {
      payload.tools = [{
        functionDeclarations: tools.map(tool => this.formatToolForGemini(tool))
      }];
    }

    // Add files if provided
    if (files && files.length > 0) {
      payload.contents = this.addFilesToContents(payload.contents, files);
    }

    const url = `${this.baseUrl}/${model}:streamGenerateContent?alt=sse&key=${this.apiKey}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(`Gemini API streaming error: ${response.status}. Details: ${JSON.stringify(errorBody)}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              break;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.candidates && parsed.candidates[0]?.content?.parts?.[0]?.text) {
                const chunkText = parsed.candidates[0].content.parts[0].text;
                fullContent += chunkText;

                // Call the chunk callback if provided
                if (onChunk) {
                  onChunk({
                    text: chunkText,
                    fullContent: fullContent,
                    done: false
                  });
                }
              }
            } catch (e) {
              // Skip invalid JSON chunks
              console.debug('Skipping invalid JSON chunk:', data);
            }
          }
        }
      }

      // Final chunk to indicate completion
      if (onChunk) {
        onChunk({
          text: '',
          fullContent: fullContent,
          done: true
        });
      }

      return {
        success: true,
        content: fullContent,
        rawResponse: null
      };

    } catch (error) {
      console.error('Gemini API streaming error:', error);
      return {
        success: false,
        error: error.message,
        rawResponse: null
      };
    }
  }

  formatToolForGemini(tool) {
    return {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters || {
        type: "object",
        properties: {},
        required: []
      }
    };
  }

  async executeFunctionCalls(functionCalls) {
    const results = [];
    
    for (const call of functionCalls) {
      const { name, args } = call.functionCall;
      const tool = this.tools.find(t => t.name === name);
      
      if (tool && tool.execute) {
        try {
          const result = await tool.execute(args);
          results.push({
            functionResponse: {
              name,
              response: result
            }
          });
        } catch (error) {
          results.push({
            functionResponse: {
              name,
              response: {
                error: error.message
              }
            }
          });
        }
      }
    }
    
    return results;
  }

  async sendFunctionResults(previousMessages, functionResults, options) {
    // Add function results to conversation
    const updatedContents = [
      ...this.formatMessages(previousMessages, options.systemPrompt),
      {
        role: 'model',
        parts: functionResults
      }
    ];

    const payload = {
      contents: updatedContents,
      generationConfig: {
        temperature: options.temperature || 0.7,
        maxOutputTokens: options.maxTokens || 8192
      }
    };

    const url = `${this.baseUrl}/${options.model || this.defaultModel}:generateContent?key=${this.apiKey}`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    
    if (result.candidates?.[0]?.content?.parts?.[0]) {
      return {
        success: true,
        content: result.candidates[0].content.parts[0].text,
        functionResults,
        rawResponse: result
      };
    }
    
    return {
      success: false,
      error: 'Failed to get response after function execution',
      rawResponse: result
    };
  }

  addFilesToContents(contents, files) {
    // Add files as inline data to the first user message
    if (contents.length > 0 && files.length > 0) {
      const lastUserMessage = contents[contents.length - 1];
      
      const fileParts = files.map(file => ({
        inlineData: {
          mimeType: file.mimeType,
          data: file.data // base64 encoded
        }
      }));
      
      lastUserMessage.parts = [
        ...fileParts,
        ...lastUserMessage.parts
      ];
    }
    
    return contents;
  }

  formatMessages(messages, systemPrompt = '') {
    const contents = [];
    
    // Add system prompt as first message if provided
    if (systemPrompt) {
      contents.push({
        role: 'user',
        parts: [{ text: `System: ${systemPrompt}\n\nPlease follow the system instructions above for all responses.` }]
      });
      contents.push({
        role: 'model',
        parts: [{ text: 'Understood. I will follow the system instructions.' }]
      });
    }

    // Convert messages to Gemini format
    messages.forEach(msg => {
      const role = msg.role === 'assistant' ? 'model' : 'user';
      
      // Handle messages with images or files
      if (msg.files && msg.files.length > 0) {
        const parts = msg.files.map(file => ({
          inlineData: {
            mimeType: file.mimeType,
            data: file.data
          }
        }));
        
        if (msg.content) {
          parts.push({ text: msg.content });
        }
        
        contents.push({ role, parts });
      } else {
        contents.push({
          role,
          parts: [{ text: msg.content }]
        });
      }
    });

    return contents;
  }

  // Special method for image generation
  async generateImage(prompt, options = {}) {
    const model = 'gemini-2.0-flash-exp';
    const url = `${this.baseUrl}/${model}:generateContent?key=${this.apiKey}`;
    
    const payload = {
      contents: [{
        parts: [{
          text: `Generate an image: ${prompt}`
        }]
      }],
      generationConfig: {
        temperature: 0.4,
        maxOutputTokens: 8192,
        responseMimeType: "image/png"
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (result.candidates?.[0]?.content?.parts?.[0]?.inlineData) {
        return {
          success: true,
          image: result.candidates[0].content.parts[0].inlineData,
          prompt
        };
      }
      
      return {
        success: false,
        error: 'Failed to generate image'
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }
}
